import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import ReactGA from "react-ga4";
import { motion } from "framer-motion";
import { HttpStatusCode } from "axios";
import { useTranslation } from "react-i18next";

import LanguageSwitcherFixed from "./LanguageSwitcher";

import {
  ShowToastModal,
  ToggleLoader,
  ToggleLoginSignupModal,
} from "../redux/uiReducer";
import { Login } from "../redux/authReducer";
import { useUIContext } from "../Context/UIContextProvider";

import { loginService } from "../WebApiServices/login.service";
import { archiveActionService } from "../WebApiServices/archiveAction.service";

import {
  AddLocalStorageData,
  GetCurrentYear,
  GetLoggedInUserId,
  IsEmailValid,
  IsStringEmpty,
} from "../Helpers/Utilities";
import {
  ARCHIVE_ACTIONS,
  PROD_DOMAIN,
  TOAST_TYPE,
  USER,
} from "../Helpers/Enums";
import { helperText } from "../Helpers/helperText";

const { SUBSCRIPTION_NOT_STARTED, SUBSCRIPTION_ENDED, ONE_DAY_LEFT } =
  USER.SUBSCRIPTION_STATUS;

const {
  apiErrorMessage,
  notSubscribed,
  oneDayLeft,
  subscriptionEnded,
  ssnetUser,
} = helperText.global;

const LoginSignupModal = () => {
  const { t: translation } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isLoginClicked, setIsLoginClicked] = useState(false);
  const [formData, setFormData] = useState({
    loginEmail: "",
    errorloginEmail: false,
    loginPassword: "",
    errorloginPassword: false,
    showLoginPasswordEye: true,
    isLoginTermsChecked: false,
    errorloginTerms: false,
    loginCaptchaText: "",
    errorloginCaptchaText: false,

    signUpFirstName: "",
    errorsignUpFirstName: false,
    signUpLastName: "",
    errorsignUpLastName: false,
    signUpEmail: "",
    errorsignUpEmail: false,
    signUpPassword: "",
    errorsignUpPassword: false,
    signUpCaptchaText: "",
    errorsignUpCaptchaText: false,
    showSignUpPasswordEye: true,
    isSignUpTermsChecked: false,
    errorSignUpTerms: false,
  });

  const labels = translation("pages.login.labels", {
    returnObjects: true,
  });

  const messages = translation("pages.login.messages", {
    returnObjects: true,
  });

  /* <summary>
  date: 12-07-2023
  Name: VK
  description: GA for Login & SignUp
  <summary>*/
  const googleAnalyticLoginEvent = () => {
    // Check if the current domain matches the allowed domain
    if (window.location.hostname === PROD_DOMAIN) {
      ReactGA.event({
        category: "Login category",
        action: "Login",
        nonInteraction: true, // optional, true/false
        transport: "xhr", // optional, beacon/xhr/image
      });
    }
  };

  const { ShowConfirmModal } = useUIContext();

  const OnTextChange = (e) => {
    const { value, name } = e.target;
    const errorState = `error${name}`;
    setFormData((t) => ({ ...t, [name]: value, [errorState]: false }));
  };

  /* <summary>
  date: 21-08-2023
  Name: GM
  description: Removed captcha check logic
  <summary>*/
  const IsUserLoginFormValid = () => {
    const _formData = formData;
    let isValid = true;

    if (!IsEmailValid(formData.loginEmail)) {
      isValid = false;
      _formData.errorloginEmail = true;
    }

    if (IsStringEmpty(formData.loginPassword)) {
      isValid = false;
      _formData.errorloginPassword = true;
    }

    if (!formData.isLoginTermsChecked) {
      isValid = false;
      _formData.errorloginTerms = true;
    }

    setFormData(_formData);
    return isValid;
  };

  /* <summary>
  date: 21-08-2023
  Name: GM
  description: Invisible captcha logic added
  <summary>*/
  const OnLoginButtonClicked = async () => {
    localStorage.removeItem("logout");
    localStorage.setItem("lastActivityTime", Date.now().toString());

    if (IsUserLoginFormValid()) {
      AuthenticateUser();
    } else {
      ShowToast(messages.requiredFields, TOAST_TYPE.Error);
    }
  };

  useEffect(() => {
    if (isLoginClicked) {
      if (IsUserLoginFormValid()) {
        googleAnalyticLoginEvent();
        AuthenticateUser();
      } else {
        ShowToast(messages.requiredFields, TOAST_TYPE.Error);
      }
    }

    return () => {};
  }, [isLoginClicked]);

  const AuthenticateUser = async () => {
    try {
      dispatch(ToggleLoader(true));

      const response = await loginService.AuthenticateUser({
        email: formData.loginEmail,
        password: formData.loginPassword,
      });

      dispatch(ToggleLoader(false));

      if (response.status.code === "ERR_NETWORK") {
        throw new Error(messages.networkError);
      }

      if (response.status === HttpStatusCode.BadRequest) {
        throw new Error(messages.login);
      }

      if (response.status === HttpStatusCode.Unauthorized) {
        throw new Error(response.data.data);
      }

      if (response.status !== HttpStatusCode.Ok) {
        throw new Error(messages.login);
      }

      const { userStatus, userSubscriptionStatus, ssnetUserLogin } =
        response.data.userDetails;

      // Store isOnline in local storage
      AddLocalStorageData("isOnline", response.data.isOnline);

      if (ssnetUserLogin) {
        dispatch(ToggleLoginSignupModal());
        throw new Error(ssnetUser);
      }

      switch (userStatus) {
        case USER.STATUS.ACTIVE: {
          let assignData = false;

          if (userSubscriptionStatus === SUBSCRIPTION_ENDED) {
            dispatch(ToggleLoginSignupModal());
            throw new Error(subscriptionEnded);
          }

          if (userSubscriptionStatus === SUBSCRIPTION_NOT_STARTED) {
            dispatch(ToggleLoginSignupModal());
            throw new Error(notSubscribed);
          }

          if (userSubscriptionStatus === ONE_DAY_LEFT) {
            dispatch(ToggleLoginSignupModal());

            const choice = await ShowConfirmModal("", oneDayLeft, "OK", true);

            if (choice) {
              navigate("/Home");
              dispatch(ToggleLoginSignupModal());
            }
          }

          assignData = true;

          if (assignData) {
            AddLocalStorageData("eUser", response.data.userDetails);
            AddLocalStorageData("authToken", response.data.authToken);
            LoggedInArchiveAction();
            dispatch(ToggleLoginSignupModal());
            dispatch(Login());
            navigate("/");
          }

          return;
        }

        case USER.STATUS.INACTIVE: {
          throw new Error(USER.MSSGS.INACTIVE);
        }

        case USER.STATUS.PENDING: {
          throw new Error(USER.MSSGS.PENDING);
        }

        default:
          throw new Error();
      }
    } catch (error) {
      ShowToast(
        (error.name !== "AxiosError" && error.message) || apiErrorMessage,
        TOAST_TYPE.Error
      );
    }
  };

  const LoggedInArchiveAction = async () => {
    try {
      const { NUMBER, DATA } = ARCHIVE_ACTIONS.LOGGED_IN;
      const userGUID = GetLoggedInUserId();

      const response = await archiveActionService.AddArchive(
        NUMBER,
        DATA,
        userGUID,
        0,
        userGUID
      );

      if (response.status !== HttpStatusCode.Ok) {
        throw response.error;
      }
    } catch (error) {
      ShowToast(ARCHIVE_ACTIONS.ERROR, TOAST_TYPE.Error);
    }
  };

  const ToggleLoginShowPasswordEyeIcon = () => {
    setFormData((t) => ({
      ...t,
      showLoginPasswordEye: !t.showLoginPasswordEye,
    }));
  };

  const OnCheckBoxChange = (e) => {
    const { name } = e.target;

    if (name === "loginTerms") {
      setFormData((prevState) => ({
        ...prevState,
        isLoginTermsChecked: !prevState.isLoginTermsChecked,
        errorloginTerms: false,
      }));
    } else {
      setFormData((prevState) => ({
        ...prevState,
        isSignUpTermsChecked: !prevState.isSignUpTermsChecked,
        errorSignUpTerms: false,
      }));
    }
  };

  const ShowToast = (toastMessage, toastType) => {
    dispatch(
      ShowToastModal({
        toastMessage,
        toastType,
      })
    );
  };

  const {
    loginEmail,
    errorloginEmail,
    loginPassword,
    errorloginPassword,
    showLoginPasswordEye,
    errorloginTerms,
    isLoginTermsChecked,
  } = formData;

  return (
    <>
      <LanguageSwitcherFixed />

      <section className="login-modal">
        <div
          className="modal-backdrop fade show"
          style={{ backgroundColor: "transparent" }}
        ></div>
        <motion.div
          key="login"
          initial={{
            y: -1000,
          }}
          animate={{
            y: 0,
          }}
          exit={{
            y: -1000,
          }}
          transition={{ duration: 0.5 }}
          className="modal fade show"
          id="ModalForm"
          tabIndex={-1}
          aria-labelledby="ModalFormLabel"
          aria-modal="true"
          role="dialog"
          style={{ display: "block" }}
        >
          <div className="modal-dialog modal-dialog-centered login-modal-dialog">
            <div className="modal-content">
              <div className="modal-body">
                <div className="login-main">
                  {/* LOGIN SECTION */}
                  <div className="login">
                    <form>
                      <div className="d-flex justify-content-center">
                        <div className="d-flex flex-column align-items-center gap-2">
                          <img
                            src="assets/images/seafarer-logo-notext.svg"
                            width={120}
                            alt="Seafarer LMS"
                            className="mt-4"
                          />

                          <h1
                            style={{
                              color: "var(--custom-primary)",
                              letterSpacing: "1px",
                              fontSize: "30px",
                            }}
                          >
                            {translation("logo.seafarer").toUpperCase()}{" "}
                            <span className="secondary-text fw-bolder">
                              {translation("logo.lms").toUpperCase()}
                            </span>
                          </h1>

                          <h2
                            style={{
                              color: "#dedede",
                              letterSpacing: "1px",
                              fontWeight: "600",
                            }}
                          >
                            {labels.clientPortal.toUpperCase()}
                          </h2>
                        </div>
                      </div>

                      {/* EMAIL */}
                      <div
                        className={errorloginEmail ? "login-validation" : ""}
                      >
                        <input
                          type="email"
                          name="loginEmail"
                          placeholder={labels.email}
                          required
                          onChange={OnTextChange}
                          value={loginEmail}
                        />
                        {errorloginEmail && (
                          <i
                            className="fa fa-exclamation-circle error-icon fs-3"
                            aria-hidden="true"
                          />
                        )}
                      </div>

                      {/* PASSWORD */}
                      <div
                        className={
                          errorloginPassword
                            ? "login-validation"
                            : "password-eye"
                        }
                      >
                        <input
                          type={showLoginPasswordEye ? "password" : "text"}
                          name="loginPassword"
                          placeholder={labels.password}
                          required
                          value={loginPassword}
                          onChange={OnTextChange}
                        />
                        {errorloginPassword && (
                          <i
                            className="fa fa-exclamation-circle error-icon fs-3"
                            aria-hidden="true"
                          />
                        )}
                        {!errorloginPassword && (
                          <i
                            onClick={ToggleLoginShowPasswordEyeIcon}
                            className={`fa ${
                              showLoginPasswordEye ? "fa-eye" : "fa-eye-slash"
                            } eye-icon`}
                            id="togglePassword"
                          />
                        )}
                      </div>

                      {/* TERMS & CONDITIONS */}
                      <div className={`form-group form-checkbox`}>
                        <input
                          type="checkbox"
                          name="loginTerms"
                          checked={isLoginTermsChecked}
                          onChange={OnCheckBoxChange}
                          className="checkbox"
                        />
                        <span className="check-label">
                          {labels.agree}{" "}
                          <a href="/TermsAndConditions" target="_blank">
                            {labels.terms}
                          </a>
                        </span>
                      </div>
                      <div
                        className="error-input"
                        style={{
                          visibility: `${
                            errorloginTerms ? "visible" : "hidden"
                          }`,
                        }}
                      >
                        <span>{messages.terms}.</span>
                      </div>

                      {/* LOGIN BUTTON */}
                      <div className="login-btn">
                        <button
                          id="login-modal-btn"
                          type="button"
                          onClick={OnLoginButtonClicked}
                        >
                          {translation("labels.login").toUpperCase()}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>

                <div
                  style={{
                    color: "#dedede",
                    textAlign: "center",
                    fontWeight: 500,
                  }}
                >
                  <p>
                    ©{GetCurrentYear()}{" "}
                    <a
                      className="custom-link"
                      href="https://www.edot-solutions.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "inherit" }}
                    >
                      {translation("labels.edot")}
                    </a>
                    . {translation("labels.footerText")}.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
};

export default LoginSignupModal;
